import { useRef } from "react";

const WINDOW_MS = 2000;
const MAX_RENDERS = 60;

/**
 * Dev-only hook that warns when a component renders more than MAX_RENDERS
 * times within WINDOW_MS. Drop into any suspect component:
 *
 *   useRenderGuard('MyComponent');
 *
 * In production builds the refs are still allocated (Rules of Hooks) but the
 * tracking logic is dead-code-eliminated by the bundler.
 */
export function useRenderGuard(componentName: string): void {
  const timestamps = useRef<number[]>([]);
  const warned = useRef(false);

  if (process.env.NODE_ENV === "production") return;

  const now = Date.now();
  const ts = timestamps.current;
  ts.push(now);

  while (ts.length > 0 && ts[0] < now - WINDOW_MS) {
    ts.shift();
  }

  if (ts.length > MAX_RENDERS && !warned.current) {
    warned.current = true;
    console.error(
      `[RenderGuard] "${componentName}" rendered ${ts.length} times in ${WINDOW_MS}ms — likely infinite loop.`,
    );
  }

  if (ts.length <= MAX_RENDERS / 2) {
    warned.current = false;
  }
}
